export const query = `
    query GetFundList(
        $effectiveDate: String
        $locale: String
        $annotationFilterProperty: String = "useForWebProfiles"
        $includeProfiles: Boolean! = true
        $includeAnnotations: Boolean! = true
    ) {
        # get global documents, that are not associated with any given web profile
        globalDocument: webProfileDocuments(
            locale: $locale
            filters: [{ propertyId: "webProfile", operator: "NotContains", propertyValue: "" }]
        ) {
            entityId
            code
            name
            shortName
            url
            title
            description
            thumbnailUrl
            tags
            sortOrder
        }

        webProfiles(effectiveDate: $effectiveDate, locale: $locale, take: 9999)
            @include(if: $includeProfiles) {
            entityId
            fundName
            sortOrder
            isInactive

            documents {
                entityId
                code
                name
                shortName
                url
                title
                description
                thumbnailUrl
                tags
                sortOrder
            }

            webProfileSalesOptions(take: 1) {
                salesOption {
                    series {
                        ...fundListPrimarySeriesFields
                        fund {
                            entityId
                            fullName
                            aum
                            assetClass {
                                entityId
                                name
                                shortName
                                fullName
                                sortOrder
                            }
                            fundCategory {
                                entityId
                                name
                                shortName
                                fullName
                                sortOrder
                            }
                            product {
                                entityId
                                name
                                shortName
                                fullName
                                sortOrder
                            }
                            productGroup {
                                entityId
                                name
                                shortName
                                fullName
                                sortOrder
                            }
                            cifscCategory {
                                ...fundListCifscCategoryFields
                            }
                            riskRating {
                                ...fundListRiskRatingFields
                            }
                            series {
                                entityId
                                seriesType {
                                    ...fundListSeriesTypeFields
                                }
                                fundCodes: salesOptions {
                                    entityId
                                    fundservCode
                                    loadType {
                                        ...fundListLoadTypeFields
                                    }
                                    product {
                                        entityId
                                        name
                                        shortName
                                        fullName
                                        sortOrder
                                    }
                                    productGroup {
                                        entityId
                                        name
                                        shortName
                                        fullName
                                        sortOrder
                                    }
                                }
                                performance: salesOptions(take: 1) {
                                    compoundPerformance(take: 1, sortDirection: "D") {
                                        ...fundListCompoundPerformanceFields
                                    }
                                }
                            }
                        }
                    }
                    ...fundListSalesOptionPerformanceFields
                }
            }
        }
        globalWebProfileAnnotations: webProfileAnnotations(
            locale: $locale
            filters: [
                { propertyId: $annotationFilterProperty, operator: "=", propertyValue: "true" }
                { propertyId: "isGlobal", operator: "=", propertyValue: "true" }
            ]
        ) @include(if: $includeAnnotations) {
            ...fundListWebProfileAnnotationFields
        }
        globalFundAnnotations: fundAnnotations(
            locale: $locale
            filters: [
                { propertyId: $annotationFilterProperty, operator: "=", propertyValue: "true" }
                { propertyId: "isGlobal", operator: "=", propertyValue: "true" }
            ]
        ) @include(if: $includeAnnotations) {
            ...fundListFundAnnotationFields
        }
        globalSeriesAnnotations: seriesAnnotations(
            locale: $locale
            filters: [
                { propertyId: $annotationFilterProperty, operator: "=", propertyValue: "true" }
                { propertyId: "isGlobal", operator: "=", propertyValue: "true" }
            ]
        ) @include(if: $includeAnnotations) {
            ...fundListSeriesAnnotationFields
        }
    }

    fragment fundListSeriesTypeFields on SeriesType {
        entityId
        name
        shortName
        fullName
        description
        sortOrder
    }

    fragment fundListLoadTypeFields on LoadType {
        entityId
        name
        code
    }

    fragment fundListRiskRatingFields on RiskRating {
        entityId
        name
        sortOrder
    }

    fragment fundListCompoundPerformanceFields on CompoundPerformance {
        effectiveDate
        p1mo
        p3mo
        p6mo
        pytd
        p1yr
        p2yr
        p3yr
        p4yr
        p5yr
        p7yr
        p8yr
        p9yr
        p10yr
        p15yr
        psi
        psiB1
        psiB2
        psiB3
        best3mo
        best3moEffectiveDate
        worst3mo
        worst3moEffectiveDate
    }

    fragment fundListSalesOptionPerformanceFields on SalesOption {
        entityId

        _performance_dailyPerformance {
            effectiveDate
            navPS
            price
            d1EffectiveDate
            d1NavPs
            d1Amt
            d1Pct
            d1Price
        }

        compoundPerformance(take: 1, sortDirection: "D") {
            ...fundListCompoundPerformanceFields
        }
    }

    fragment fundListCifscCategoryFields on CifscCategory {
        name
        description
        geographicRegion
        sortOrder
    }

    fragment fundListCurrencyFields on Currency {
        entityId
        name
        iso4217alpha
        iso4217num
    }

    fragment fundListFeesFields on Fees {
        effectiveDate
        mer
        isEstimatedMer
        managementFee
        insuranceFee
        adminFee
        ter
        fixedAdminFee
        hst
    }

    fragment fundListMorningStarSeriesRatingsFields on MorningStarSeriesRating {
        entityId
        effectiveDate
        overAllRating
        overAllNumFund
        _1moRating
        _3moRating
        _6moRating
        _1yrRating
        _3yrRating
        _5yrRating
        _10yrRating
        numFund1mo
        numFund3mo
        numFund6mo
        numFund1yr
        numFund3yr
        numFund5yr
        numFund10yr
    }

    fragment fundListPrimarySeriesFields on Series {
        entityId
        inceptionDate
        fees(sortDirection: "d", take: 1) {
            ...fundListFeesFields
        }
        fundStatus {
            entityId
            name
            shortName
            sortOrder
        }
        seriesType {
            ...fundListSeriesTypeFields
        }
        currency {
            ...fundListCurrencyFields
        }
        inceptionDate
        morningStarSeriesRatings(take: 1, sortDirection: "d") {
            ...fundListMorningStarSeriesRatingsFields
        }
    }

    fragment fundListFundAnnotationFields on FundAnnotation {
        id: entityId
        text
        textHtml
        textTargetId
        markerTargetId
        markerType
        marker
    }

    fragment fundListWebProfileAnnotationFields on WebProfileAnnotation {
        id: entityId
        text
        textHtml
        textTargetId
        markerTargetId
        markerType
        marker
    }

    fragment fundListSeriesAnnotationFields on SeriesAnnotation {
        id: entityId
        text
        textHtml
        textTargetId
        markerTargetId
        markerType
        marker
    }
`;

export const transform = ``;
